import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle, ArrowUpRight, Envelope } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { fetchBooking } from "@/lib/api";

const ConfirmationPage = () => {
  const { reference } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchBooking(reference).then(setBooking).catch(() => setError(true));
  }, [reference]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Nav />
      <main className="flex-1 px-6 md:px-12 lg:px-24 py-20 lg:py-28" data-testid="confirmation-section">
        {error ? (
          <div>
            <div className="eyebrow">Not found</div>
            <h1 className="font-display text-4xl text-ink-900 mt-4">We couldn&apos;t locate that booking.</h1>
            <Link to="/book">
              <Button className="mt-8 rounded-none bg-ink-900 hover:bg-ink-800 text-white px-8 py-6 font-mono text-xs tracking-[0.2em] uppercase">
                Start a new booking
              </Button>
            </Link>
          </div>
        ) : !booking ? (
          <div className="font-mono text-xs uppercase tracking-[0.22em] text-muted-foreground">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7 reveal">
              <CheckCircle size={56} weight="duotone" className="text-champagne" />
              <div className="eyebrow mt-8">Confirmation</div>
              <h1 className="font-display text-5xl lg:text-6xl text-ink-900 mt-4 leading-tight">
                Your videographer is
                <br />
                <span className="italic text-champagne">reserved.</span>
              </h1>
              <p className="mt-8 max-w-xl text-muted-foreground leading-relaxed">
                A confirmation has been sent to{" "}
                <span className="text-ink-900">{booking.contact_email}</span>. A coordinator will reply
                personally within one business hour to confirm the videographer assignment, equipment,
                and parking notes.
              </p>

              <div className="mt-12 inline-flex items-center gap-4 bg-ink-900 text-white px-8 py-5">
                <Envelope size={22} className="text-champagne" />
                <div>
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-champagne">Reference</div>
                  <div className="font-display text-2xl" data-testid="confirmation-reference">{booking.reference}</div>
                </div>
              </div>

              <div className="mt-12 flex flex-wrap gap-4">
                <Link to="/" data-testid="back-home-btn">
                  <Button variant="outline" className="rounded-none border-ink-900 px-8 py-6 font-mono text-xs tracking-[0.2em] uppercase">
                    Back to home
                  </Button>
                </Link>
                <Link to="/book" data-testid="new-booking-btn">
                  <Button className="rounded-none bg-ink-900 hover:bg-ink-800 text-white px-8 py-6 font-mono text-xs tracking-[0.2em] uppercase">
                    Book another <ArrowUpRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            <aside className="lg:col-span-5">
              <div className="bg-paper-warm border border-border p-8">
                <div className="eyebrow">Booking summary</div>
                <dl className="mt-6 space-y-5 text-sm">
                  <Row label="Status" value={<span className="text-champagne font-mono text-xs tracking-[0.18em] uppercase">{booking.status}</span>} />
                  <Row label="Date" value={`${booking.scheduled_date} · ${booking.scheduled_time}`} />
                  <Row label="Duration" value={`${booking.duration_hours} hour(s)`} />
                  <Row label="Location" value={booking.location_type.replace("_", " ")} />
                  <Row label="Case" value={booking.case_caption} />
                  <Row label="Deponent" value={booking.deponent_name} />
                  <Row label="Attorney" value={`${booking.attorney_name}, ${booking.firm_name}`} />
                </dl>
              </div>
            </aside>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between items-start gap-4">
    <dt className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground pt-1">{label}</dt>
    <dd className="text-right text-ink-900 capitalize">{value}</dd>
  </div>
);

export default ConfirmationPage;
